import React from "react"
import { graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import parse from "html-react-parser"
import styled from "styled-components"
import "@wordpress/block-library/build-style/common.css"
import "@wordpress/block-library/build-style/style.css"
import "@wordpress/block-library/build-style/theme.css"

import Layout from "../components/layout"
import Seo from "../components/seo"
import transformationBackground from "../images/transformation-bg.jpg"
import FullPageFrame from "../components/fullPageFrame"
import DiscoveryContent from "../components/discovery/content"
import CK1618573 from "../components/forms/ck-1618573"
import ResetTemplate from "./reset"
import ResetArchive from "./resetArchive"
import TopDoc from "./topDoc"
import CTAformPage from "./ctaFormPage"
import NonSurgicalTreatment from "../pages/non-surgical-treatments"
import AwardsHeroTemplate from "./awards"

const Hero = styled.section`
  position: fixed;
  top: 56px;
  width: 100%;
  height: calc(100vh - 56px);
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;

  .gatsby-image-wrapper img {
    object-position: 75% top;
  }

  @media (max-width: 739px) {
    .gatsby-image-wrapper {
      top: 100px !important;
    }
  }
`


const PageHeader = styled.div`
margin-top: -66px;
padding-top: 66px;
padding-bottom: 66px;
background-color: #fff;
background-image: url("../images/reset/reset-single-hero-bg.png");
background-repeat: no-repeat;
background-size: cover;
background-position: top center;
`

const PageTitle = styled.h1`
display: flex;
flex-direction: column;
align-items: center;
padding: 60px 1rem 0;
margin: 0 auto;
max-width: 1060px;
text-align: center;
font-size: clamp(2rem, -0.6471rem + 7.0588vw, 5rem);
`

const BgContainer = styled.div`
  background-position: center top;
`

const PageTemplate = ({ data: { page, site }, location }) => {
  if ( page.resetTemplateFields.useResetFields ) {
    return ( <ResetTemplate page={page} /> );
  }

  if ( 9726 === page.databaseId ) {
    return ( <ResetArchive page={page} /> );
  }

  const laserTemplates = [
    "laser-eyelid-festoons",
    "facial-plastic-surgery-in-tampa/reset-skin-resurfacing",
    "non-invasive-laser",
    "lost-facial-volume",
  ]

  const topDocID = 9914;

  if ( topDocID === page.databaseId ) {
    return ( <TopDoc data={ { page, site } } /> );
  }

  const heroFields = page.heroControls?.heroFields || {};
  const heroLayouts = heroFields?.layout || [];
  const heroLayout = heroLayouts[0] || '';

  switch (heroLayout) {
    case 'awards':
      return ( <AwardsHeroTemplate data={ { page, site, hero: heroFields } } /> );
  }

  const slug = page.uri.replace("/", "")

  if ('non-surgical-treatments' === slug) {
    return (<NonSurgicalTreatment location={location} />);
  }

  if (laserTemplates.includes(slug)) {
    return (
      <>
        <Seo title={page.seo.title} description={page.seo.metaDesc} seo={page.seo} />
        <FullPageFrame title={page.title} slug={slug} />
      </>
    )
  }

  const fullPageWithFrame = [
    'video-ask',
  ];

  if ( fullPageWithFrame.includes(slug) ) {
    const FullPageWithFrame = styled.div`
      &,
      iframe {
        height: 100%;
        width: 100vw;
        inset: 0;
        border: none;
        position: fixed;
        top: 0;
        margin-bottom: 40px;
      }
    `
    return (
      <>
        <Seo title={page.seo.title} description={page.seo.metaDesc} seo={page.seo} />
        <FullPageWithFrame>
          {parse(page.content || '')}
        </FullPageWithFrame>
      </>
    )
  }

  const handleShortcodes = node => {
    try {
      const shortcode = node.data || '';
      if (shortcode && shortcode.includes('convertkit form=161857')) {
        return (<CK1618573 />);
      }
    } catch {}

    return node;
  }

  let ContentWrap = styled.div`
    margin: 0 auto;
    padding: 0.45rem 0;

    > section {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    > section > * {
      max-width: 1080px;
      width: 100%;
      padding: 0 1.0875rem;

      &.alignfull {
        max-width: 100%;
      }

      .is-layout-constrained {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 100%;

        > * {
          max-width: 1080px;
          width: 100%;
        }
      }

      &.alignwide {
        max-width: 1240px;
      }
    }

    .wp-block-cover-image .wp-block-cover__image-background, .wp-block-cover-image video.wp-block-cover__video-background, .wp-block-cover .wp-block-cover__image-background, .wp-block-cover video.wp-block-cover__video-background {
      position: absolute !important;
    }

    .wp-block-cover {
      margin-bottom: 2rem;
    }

    iframe[src="https://msgsndr.com/widget/form/Y42Bqdn1CtVHr4refUS9"]
    {
      min-height: 1100px;

      @media (max-width: 600px) {
        min-height: 1450px;
      }

      @media (max-width: 350px) {
        min-height: 1550px;
      }
    }

    font-size: clamp(1.125rem, 0.9044rem + 0.5882vw, 1.375rem);

    p, li {
      font-size: clamp(1.125rem, 0.9044rem + 0.5882vw, 1.375rem);
      line-height: 1.5;
    }
  `

  if ("Patient Testimonials" === page.title) {
    ContentWrap = styled.div`
      padding: 0 30px;

      section {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;
      }

      hr {
        width: 100%;
      }

      center {
        width: 80%;
        margin: 10px 10%;
        position: relative;
        height: 0;
        padding-top: 45%;

        iframe {
          width: 100%;
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          bottom: 0;
          right: 0;
        }
      }

      p {
        width: 31%;
      }
    `
  }

  const vimeoMatch = /<div class="[^"]*container-vimeo[^>]+>[^>]+>(https?:\/\/player.vimeo.com\/video\/[^<]+).+?<\/div>/g
  const vimeoReplace =
    '<iframe src="$1" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>'

  const ytMatch = /<div class="[^"]*container-youtube[^>]+>[^>]+>https?:\/\/www.youtube.com\/watch\?v=([^&<]+).+?<\/div>/g
  const ytReplace =
    '<iframe width="560" height="315" src="https://www.youtube.com/embed/$1" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>'

  const MaybeCalendly = () => {
    
    if ( 'Request Appointment' !== page.title && 9513 !== page.databaseId ) {
      return '';
    }

    if (typeof window !== "undefined") {
      let loaded = false;

      const loadCalendly = () => {
        if ( loaded ) {
          return;
        }

        const calendlyWidget = document.querySelector('.calendly-inline-widget');

        if ( calendlyWidget) {
          loaded = true;
          const calendlyScript = document.createElement("script");

          calendlyScript.setAttribute(
            "src",
            "https://assets.calendly.com/assets/external/widget.js"
          )
          calendlyScript.setAttribute("async", "async")
          calendlyWidget.parentNode.append(calendlyScript)
        }
      }

      document.addEventListener( 'scroll', loadCalendly );
    }

    return '';
  } 

  let formHook = null;

  if ("Request Appointment" === page.title || 9513 === page.databaseId) {
    switch (page.databaseId) {
      case 9513:
        formHook = 'https://hooks.zapier.com/hooks/catch/4189994/3fzx5kd/';
        break;
      default:
        formHook = 'https://hooks.zapier.com/hooks/catch/4189994/b0t6bgg/';
    }
  }

  if ( 'Request Appointment' !== page.title && 9513 !== page.databaseId && "Start Your Transformation" !== page.title ) {
    return (
      <CTAformPage data={ { post: page } } />
    )
  }

  return (
    <Layout pageID={page.databaseId} slug={slug}>
      <Seo title={page.seo.title} description={page.seo.metaDesc} seo={page.seo} />
      <style
        dangerouslySetInnerHTML={
          {
            __html: `
              .page-header:not(.sticky){
                box-shadow: none;
              }
              .page-header:not(.sticky),
              .page-header:not(.sticky) #header-menu-toggle {
                background-color: transparent;
              }

              @media (min-width: 1204px) {
                .page-header:not(.sticky) *:not(#header-menu-explore, #header-menu-explore-toggle, #search-toggle) {
                  background-color: transparent;
                }
              }
            `
          }
        }
      />
      <article
        className="blog-post"
        itemScope
        itemType="http://schema.org/Article"
      >
        {
          'Request Appointment' === page.title || 9513 === page.databaseId ?
            <Hero className="appt-hero">
              <style dangerouslySetInnerHTML={ { __html: `
                body {
                  scroll-snap-type: y mandatory;
                }

                section[itemprop="articleBody"] > div {
                  background-color: rgba(255,255,255,.8);
                  padding: 1rem;
                  z-index: 2;
                }

                .blog-post > *,
                main + footer {
                  scroll-snap-align: start;
                  scroll-margin-top: 56px;
                }

                @media (max-width: 1000px) {
                  body {
                    scroll-snap-type: y proximity;
                  }

                  main + footer {
                    scroll-snap-align: none;
                  }
                }
              `} } />
              {
                9513 === page.databaseId ?
                  <div
                    style={{
                      position: `fixed`,
                      top: `56px`,
                      left: 0,
                      display: `flex`,
                      justifyContent: `center`,
                      alignItems: `center`,
                      height: `calc(100vh - 56px)`,
                      width: `100%`,
                      zIndex: -1,
                    }}
                  >
                    <style dangerouslySetInnerHTML={ { __html: `
                      @media (max-width: 1000px) {
                        .appt-hero {
                          height: auto !important;
                          margin-bottom: -56px !important;
                        }

                        .appt-hero > div:first-of-type {
                          height: auto !important;
                          position: relative !important;
                          top: 56px !important;
                          margin-bottom: 50px !important;
                        }

                        .appt-hero > div:first-of-type .gatsby-image-wrapper[class] {
                          top: 0 !important;
                        }

                        .appt-hero .hero-content {
                          background-image: none !important;
                        }
                      }
                    `} } />
                    <StaticImage
                      src="../images/angie-before-after.png"
                      alt="A stunning before and after of Angie showing the power of the RESET ® Full Face Resurfacing."
                      style={{ height: "100%", width: "100%" }}
                      imgStyle={{ objectFit: "contain", objectPosition: "center top" }}
                    />
                  </div>
                :
                  <StaticImage
                    src="../images/transformation-bg.jpg"
                    alt="A series of before and after faces that were transformed by Dr Scheiner."
                    style={{
                      position: `fixed`,
                      top: `56px`,
                      height: `calc(100% - 56px)`,
                      objectFit: `cover`,
                      width: `100%`,
                      zIndex: -1,
                    }}
                  />
              }
            </Hero>
          :
            <PageHeader>
              {page.title &&
              <PageTitle itemProp="headline">{parse(page.title)}</PageTitle>
              }
            </PageHeader> 
        }
        

        <BgContainer
          style={
            "Start Your Transformation" === page.title
              ? {
                  backgroundImage: `url(${transformationBackground})`,
                }
              : (
                "Request Appointment" === page.title || 9513 === page.databaseId ? {
                  minHeight: `100vh`,
                  backgroundColor: `#fff`,
                  display: `flex`,
                  flexDirection: `column`,
                  justifyContent: `center`,
                } : {}
              )
          }
        >
          <ContentWrap
          style={
            "Request Appointment" === page.title || 9513 === page.databaseId ? {
              width: `100%`,
              padding: `40px`,
              maxWidth: `1400px`,
            } : {}
          }
          >
            {!!page.content && (
              <section itemProp="articleBody">
                {"Request Appointment" === page.title || 9513 === page.databaseId ?
                  <DiscoveryContent content={page.content || ''} hook={formHook} />
                 : 
                 parse(
                  page.content
                    .replaceAll(vimeoMatch, vimeoReplace)
                    .replaceAll(ytMatch, ytReplace),
                  {
                    replace: handleShortcodes,
                  }
                )}
              </section>
            )}
          </ContentWrap>
        </BgContainer>
      </article>
      <MaybeCalendly />
    </Layout>
  )
}

export default PageTemplate

export const pageQuery = graphql`
query PageById($id: String!) {
  page: wpPage(id: {eq: $id}) {
    id
    databaseId
    content
    title
    uri
    seo {
      canonical
      metaDesc
      metaRobotsNofollow
      metaRobotsNoindex
      opengraphAuthor
      opengraphDescription
      opengraphImage {
        altText
        localFile {
          publicURL
        }
      }
      opengraphPublisher
      opengraphSiteName
      opengraphTitle
      opengraphType
      opengraphUrl
      readingTime
      schema {
        raw
      }
      title
      twitterDescription
      twitterImage {
        altText
        localFile {
          publicURL
        }
      }
      twitterTitle
    }
    heroControls {
      heroFields {
        layout
        titleParts {
          darkText
          lightText
        }
        text
        button {
          label
          url
        }
        image {
          node {
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(width: 1360, formats: [AUTO, WEBP, AVIF])
              }
            }
          }
        }
        awards {
          award {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 300, formats: [AUTO, WEBP, AVIF])
                }
              }
            }
          }
        }
      }
    }
    resetTemplateFields {
      useResetFields
      templateLayout
      columnSectionTitle
      columnSectionColumn1Text
      columnSectionColumn2Text
      comparisonCharts {
        heading
        appearance {
          backgroundColor
          backgroundColorEnd
          backgroundImage {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 2160, formats: [AUTO, WEBP, AVIF])
                }
              }
              altText
            }
          }
          isDark
        }
        beforeAfterRow {
          center {
            icon {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 150, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
            text
          }
          left {
            after {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
            before {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
          }
          right {
            after {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
            before {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
          }
        }
        columnHeadings {
          left {
            heading
            subHeading
          }
          right {
            heading
            subHeading
          }
        }
        rows {
          left
          right
          center {
            icon {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 150, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
            text
          }
        }
      }
      generalPriceStatement
      comparisonSectionTitle
      comparisonSectionContent
      comparisonSectionButton {
        comparisonSectionButtonUrl
        comparisonSectionButtonText
      }
      treatmentPriceRange
      treatmentPriceDisclaimer
      comparisonSectionBeforeImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
            }
          }
          altText
        }
      }
      comparisonSectionAfterImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
            }
          }
          altText
        }
      }
      comparisonSectionVideo {
        heading
        embedUrl
        poster {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
              }
            }
            altText
          }
        }
      }
      ctaTitle
      ctaText
      ctaVideos {
        youtubeUrl
        compare {
          before {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                }
              }
              altText
            }
          }
          after {
            node {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
                }
              }
              altText
            }
          }
        }
      }
      ctaButton {
        ctaButtonUrl
        ctaButtonText
      }
      faqSectionTitle
      faqItems {
        itemTitle
        itemContent
      }
      gridSectionTitle
      gridSectionText
      gridSectionFields {
        itemImage {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 120, formats: [AUTO, WEBP, AVIF])
              }
            }
          }
        }
        itemLabel
      }
      sideBySideImage {
        before {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
              }
            }
            altText
          }
        }
        after {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 600, formats: [AUTO, WEBP, AVIF])
              }
            }
            altText
          }
        }
      }
    }
  }
  site {
    siteMetadata {
      title
    }
  }
}
`
